import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  Sanitizer,
  SecurityContext,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Matches URLs, URLs without scheme, HTTP query parameters in URL, phone numbers and labelled versions of aforementioned matches.
 * Expression matches domain names (not localhost), IPv4 and IPv6 (bracketed), as well as ports.
 * Labelled matches are enclosed in square brackets, containing first a label enclosed in quotes (to include a quote character in the
 * label, prefix it with '\'), then any valid (non-labelled) match.
 *
 * Examples:
 * mailto:username@example.com
 * ftp://username:password@host.co.uk
 * http://test.com:123/?url=param&another#anchor
 * [This is a label:google.com]
 * domain.name
 * ["This is a \"labelled\" phone number":+46123456789]
 * 0046123456789
 * user@host.tld
 * 127.0.0.1
 * 192.168.1.1:8080
 * [1:2:3:4:5:6:7:8]
 * [1::]:8080
 * http://[1f::2]:443/asdf?test=5&yes
 * [Labelled link-local IPv6 FTP server resource:ftp://[fe80::7:8%eth0]:21/filename.txt]
 * [This is a "labelled" server:https://username:password@subdomain.domain.com:1234/foo/bar?asdf=test&1234#anchor-1]
 *
 * Groups: (11-19 will be empty when label exists, 2-10 will be empty when there is no label)
 *  0: Whole match
 *  1      : Label
 *  2  | 12: Label-less part (same as group 0 for label-less strings)
 *  3  | 13: Scheme
 *  4  | 14: Username
 *  5  | 15: Password
 *  6  | 16: Host
 *  7  | 17: Port
 *  8  | 18: Path
 *  9  | 19: Query params
 *  10 | 20: HTML Anchor
 *  11 | 21: Phone number
 */
// eslint-disable-next-line no-useless-escape
const MATCH_ALL = /(?:\[((?:\\:|[^:])+?):((?:(?:([\d\w]*?):(?:\/\/)?)?(?:([^\s:@]*?)(?::([^\s:@]*?))?@)?)?(\[(?:(?:[0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,7}:|(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,5}(?::[0-9a-fA-F]{1,4}){1,2}|(?:[0-9a-fA-F]{1,4}:){1,4}(?::[0-9a-fA-F]{1,4}){1,3}|(?:[0-9a-fA-F]{1,4}:){1,3}(?::[0-9a-fA-F]{1,4}){1,4}|(?:[0-9a-fA-F]{1,4}:){1,2}(?::[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(?:(?::[0-9a-fA-F]{1,4}){1,6})|:(?:(?::[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(?::[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(?:ffff(?::0{1,4}){0,1}:){0,1}(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])|(?:[0-9a-fA-F]{1,4}:){1,4}:(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9]))\]|\d{1,3}(?:\.\d{1,3}){3}|(?:[^.\s\/:@]+(?:\.[^.\s\/:@]+)+))(?::(\d{1,5}))?(\/[^\s&?]*)?(\?(?:[^&=?#\s]+(?:=[^&=?#\s]+)?(?:&(?:[^&=?#\s]+(?:=[^&=?#\s]+)?))*)?)?(#[^\s#?&=]+)?|((?:tel:)?(?:[\+]|00)?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}))\]|((?:(?:([\d\w]*?):(?:\/\/)?)?(?:([^\s:@]*?)(?::([^\s:@]*?))?@)?)?(\[(?:(?:[0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,7}:|(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,5}(?::[0-9a-fA-F]{1,4}){1,2}|(?:[0-9a-fA-F]{1,4}:){1,4}(?::[0-9a-fA-F]{1,4}){1,3}|(?:[0-9a-fA-F]{1,4}:){1,3}(?::[0-9a-fA-F]{1,4}){1,4}|(?:[0-9a-fA-F]{1,4}:){1,2}(?::[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(?:(?::[0-9a-fA-F]{1,4}){1,6})|:(?:(?::[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(?::[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(?:ffff(?::0{1,4}){0,1}:){0,1}(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])|(?:[0-9a-fA-F]{1,4}:){1,4}:(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9]))\]|\d{1,3}(?:\.\d{1,3}){3}|(?:[^.\s\/:@]+(?:\.[^.\s\/:@]+)+))(?::(\d{1,5}))?(\/[^\s&?]*)?(\?(?:[^&=?#\s]+(?:=[^&=?#\s]+)?(?:&(?:[^&=?#\s]+(?:=[^&=?#\s]+)?))*)?)?(#[^\s#?&=]+)?|((?:tel:)?(?:[\+]|00)?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6})))/g;
const MATCH_PHONE = /^tel:/g;
const MATCH_IP = /^\[(?:(?:[0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,7}:|(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|(?:[0-9a-fA-F]{1,4}:){1,5}(?::[0-9a-fA-F]{1,4}){1,2}|(?:[0-9a-fA-F]{1,4}:){1,4}(?::[0-9a-fA-F]{1,4}){1,3}|(?:[0-9a-fA-F]{1,4}:){1,3}(?::[0-9a-fA-F]{1,4}){1,4}|(?:[0-9a-fA-F]{1,4}:){1,2}(?::[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(?:(?::[0-9a-fA-F]{1,4}){1,6})|:(?:(?::[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(?::[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(?:ffff(?::0{1,4}){0,1}:){0,1}(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])|(?:[0-9a-fA-F]{1,4}:){1,4}:(?:(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(?:25[0-5]|(?:2[0-4]|1{0,1}[0-9]){0,1}[0-9]))\]|\d{1,3}(?:\.\d{1,3}){3}$/;

enum MatchField {
  LABEL = 1,
  HREF = 2,
  SCHEME = 3,
  USERNAME = 4,
  PASSWORD = 5,
  HOST = 6,
  PORT = 7,
  PATH = 8,
  QUERY = 9,
  ANCHOR = 10,
  PHONE = 11,
}

const MATCH_OFFSET = 10;

// Scheme to insert if it is omitted in expression
const SCHEME_DEFAULT_IP = "http://";
const SCHEME_DEFAULT_EMAIL = "mailto:";
const SCHEME_DEFAULT_OTHER = "https://";
const SCHEME_DEFAULT_TEL = "tel:";

@Component({
    selector: 'app-text-format',
    templateUrl: './text-format.component.html',
    styleUrls: ['./text-format.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true
})
export class TextFormatComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() text: string;

  processedText: string = '';

  constructor(private changeDetectorRef: ChangeDetectorRef, private sanitizer: DomSanitizer) {}

  ngOnInit() {}

  ngAfterViewInit(): void {
    this.processText();
    this.changeDetectorRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.text) {
      this.processText();
    }
  }

  private processText() {
    this.processedText = this.text?.replace(MATCH_ALL, this.processResourceIndicator);
  }

  // TODO: Maybe map matches to custom type?
  private processResourceIndicator = (...matches: string[]) => {
    const hasLabel = !!matches[MatchField.LABEL];
    const labelOffset = hasLabel ? 0 : MATCH_OFFSET;

    let href = matches[labelOffset + MatchField.HREF];
    const label = hasLabel ? matches[MatchField.LABEL] : href;

    const scheme =
      matches[labelOffset + MatchField.PHONE] ?
        href.match(MATCH_PHONE) === null ?
        SCHEME_DEFAULT_TEL :  // "tel:" not provided in match string
        "" :                  // "tel:" explicitly provided in match string
        !matches[labelOffset + MatchField.SCHEME] ?       // Not a phone number and scheme is missing
          matches[labelOffset + MatchField.USERNAME] &&
          !matches[labelOffset + MatchField.PASSWORD] &&
          !matches[labelOffset + MatchField.PORT] &&
          !matches[labelOffset + MatchField.PATH] &&
          !matches[labelOffset + MatchField.QUERY] &&
          !matches[labelOffset + MatchField.ANCHOR] ?
          SCHEME_DEFAULT_EMAIL :  // Match has shape "username@host" (probably email)
          matches[labelOffset + MatchField.HOST].match(MATCH_IP) !== null ?
          SCHEME_DEFAULT_IP :     // Match has an IP address as a host (probably http)
          SCHEME_DEFAULT_OTHER :  // Match has a domain name as a host (probably https)
          "";                     // Match string contains an explicit scheme

    href = `${scheme}${href}`;

    return `<a target="_blank" class="note" href="${this.sanitizer.sanitize(SecurityContext.URL, href)}">${this.sanitizer.sanitize(SecurityContext.HTML, label)}</a>`;
  }
}
